/**
 * 前端获客归因（session 级，无后端）。
 * 首次落地写入；invite 可在登录前补记；不随 consumeInviteCode 清除。
 */
import { extractInviteCodeFromRoute, getInviteCode } from '@/utils/invite-code';
import { getOriginLoginType } from '@/utils/auth-token';

const STORAGE_KEY = 'deepsage-analytics-acquisition';
const LAND_KEY = 'deepsage-analytics-acquisition-land';
const INVITE_LAND_KEY = 'deepsage-analytics-invite-land';
/** 链接带来邀请码后会 strip query；用 pending 跨到确认后的 afterEach 再打 invite.land */
const INVITE_LAND_PENDING_KEY = 'deepsage-analytics-invite-land-pending';

/**
 * @typedef {{
 *   inviteCode?: string,
 *   utmSource?: string,
 *   utmMedium?: string,
 *   utmCampaign?: string,
 *   referrerHost?: string,
 *   landingRoute?: string,
 *   channel?: string,
 *   lastLoginMethod?: string,
 *   capturedAt?: number,
 * }} AcquisitionSnapshot
 */

function readRaw() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** @param {AcquisitionSnapshot} next */
function writeRaw(next) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function queryParam(query, key) {
  const raw = query?.[key];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return String(value ?? '').trim();
}

function referrerHost() {
  if (typeof document === 'undefined') return '';
  try {
    const ref = String(document.referrer || '').trim();
    if (!ref) return '';
    return new URL(ref).hostname || '';
  } catch {
    return '';
  }
}

/**
 * @param {AcquisitionSnapshot} snap
 * @returns {string|undefined}
 */
function deriveChannel(snap) {
  if (snap.inviteCode) return 'invite';
  if (snap.utmSource) return String(snap.utmSource).slice(0, 64);
  const host = String(snap.referrerHost || '').toLowerCase();
  if (!host) return 'direct';
  if (host.includes('t.me') || host.includes('telegram')) return 'telegram';
  if (host.includes('twitter') || host.includes('x.com') || host === 't.co') return 'x';
  if (host.includes('google')) return 'google';
  return host.slice(0, 64);
}

/**
 * 从路由邀请码、query 和 referrer 捕获归因（首次写入为主；空字段可补）。
 * @param {{ name?: unknown, path?: string, fullPath?: string, params?: Record<string, unknown>, query?: Record<string, unknown> }} [route]
 * @returns {AcquisitionSnapshot}
 */
export function captureAcquisitionFromRoute(route) {
  const existing = readRaw() || {};
  const query = route?.query || {};
  const inviteFromLink = extractInviteCodeFromRoute(route);
  // 仅链接触发；手填邀请码走 touchAcquisitionInvite，不置 pending
  if (inviteFromLink) markInviteLandPending();
  const invite = inviteFromLink || getInviteCode() || existing.inviteCode || '';

  /** @type {AcquisitionSnapshot} */
  const next = {
    ...existing,
    inviteCode: invite || undefined,
    utmSource: queryParam(query, 'utm_source') || existing.utmSource || undefined,
    utmMedium: queryParam(query, 'utm_medium') || existing.utmMedium || undefined,
    utmCampaign: queryParam(query, 'utm_campaign') || existing.utmCampaign || undefined,
    referrerHost: existing.referrerHost || referrerHost() || undefined,
    landingRoute:
      existing.landingRoute ||
      (typeof route?.name === 'string' && route.name
        ? route.name
        : String(route?.path || route?.fullPath || '').trim()) ||
      undefined,
    capturedAt: existing.capturedAt || Date.now(),
  };
  next.channel = deriveChannel(next);
  writeRaw(next);
  return next;
}

/** 登录前手动输入邀请码时补记 */
export function touchAcquisitionInvite(code) {
  const value = String(code ?? '').trim();
  if (!value) return getAcquisition();
  const existing = readRaw() || {};
  const next = {
    ...existing,
    inviteCode: existing.inviteCode || value,
    capturedAt: existing.capturedAt || Date.now(),
  };
  next.channel = deriveChannel(next);
  writeRaw(next);
  return next;
}

/** @returns {AcquisitionSnapshot} */
export function getAcquisition() {
  const snap = readRaw() || {};
  if (!snap.inviteCode) {
    const live = getInviteCode();
    if (live) return touchAcquisitionInvite(live);
  }
  if (!snap.channel && (snap.inviteCode || snap.utmSource || snap.referrerHost)) {
    snap.channel = deriveChannel(snap);
  }
  return snap;
}

/** 挂到业务事件 props（仅非空字段） */
export function getAcquisitionProps() {
  const snap = getAcquisition();
  /** @type {Record<string, string|number>} */
  const props = {};
  if (snap.inviteCode) props.inviteCode = snap.inviteCode;
  if (snap.utmSource) props.utmSource = snap.utmSource;
  if (snap.utmMedium) props.utmMedium = snap.utmMedium;
  if (snap.utmCampaign) props.utmCampaign = snap.utmCampaign;
  if (snap.referrerHost) props.referrerHost = snap.referrerHost;
  if (snap.landingRoute) props.landingRoute = snap.landingRoute;
  if (snap.channel) props.channel = snap.channel;
  // 勿用 method：会与 deposit/trade 的 method（cash/crypto 等）撞名
  const loginMethod = snap.lastLoginMethod || getOriginLoginType();
  if (loginMethod) props.loginMethod = loginMethod;
  return props;
}

/**
 * @param {Record<string, unknown>} [extra]
 */
export function withAcquisitionProps(extra = {}) {
  return {
    ...getAcquisitionProps(),
    ...extra,
  };
}

/** @returns {boolean} 本页签是否已打过 acquisition.land */
export function hasTrackedAcquisitionLand() {
  if (typeof sessionStorage === 'undefined') return true;
  return sessionStorage.getItem(LAND_KEY) === '1';
}

export function markAcquisitionLandTracked() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(LAND_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * 链接带来邀请码后会重定向或 strip query；用 pending 跨到确认后的 afterEach 再打 invite.land
 */
function markInviteLandPending() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(INVITE_LAND_PENDING_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** @returns {boolean} 是否有待上报的「链接邀请进入」（读后清除） */
export function consumeInviteLandPending() {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const pending = sessionStorage.getItem(INVITE_LAND_PENDING_KEY) === '1';
    if (pending) sessionStorage.removeItem(INVITE_LAND_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}

/** @returns {boolean} 本页签是否已打过 invite.land */
export function hasTrackedInviteLand() {
  if (typeof sessionStorage === 'undefined') return true;
  return sessionStorage.getItem(INVITE_LAND_KEY) === '1';
}

export function markInviteLandTracked() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(INVITE_LAND_KEY, '1');
  } catch {
    /* ignore */
  }
}

const LOGIN_METHOD_KEY = 'deepsage-analytics-login-method';

/** @param {string} method EMAIL | GOOGLE | WALLET | … */
export function noteAnalyticsLoginMethod(method) {
  const value = String(method ?? '')
    .trim()
    .toUpperCase();
  if (!value) return;
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(LOGIN_METHOD_KEY, value.slice(0, 32));
    } catch {
      /* ignore */
    }
  }
  const existing = readRaw() || {};
  writeRaw({
    ...existing,
    lastLoginMethod: value.slice(0, 32),
    capturedAt: existing.capturedAt || Date.now(),
    channel: existing.channel || deriveChannel(existing),
  });
}

/** @returns {string} */
export function peekAnalyticsLoginMethod() {
  if (typeof sessionStorage !== 'undefined') {
    try {
      const fromSession = String(sessionStorage.getItem(LOGIN_METHOD_KEY) ?? '').trim();
      if (fromSession) return fromSession;
    } catch {
      /* ignore */
    }
  }
  return String(getOriginLoginType() ?? '').trim();
}

/** @returns {string} */
export function consumeAnalyticsLoginMethod() {
  const value = peekAnalyticsLoginMethod();
  if (typeof sessionStorage === 'undefined') return value;
  try {
    sessionStorage.removeItem(LOGIN_METHOD_KEY);
  } catch {
    /* ignore */
  }
  return value;
}
